import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  useTheme,
  Stack,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  SearchOutlined,
  FilterListOutlined,
  DownloadOutlined,
  RefreshOutlined,
  ArrowForwardOutlined,
  ReceiptLongOutlined,
  CheckCircleOutlined,
  HourglassEmptyOutlined,
  UndoOutlined,
  CreditCardOutlined,
  MonetizationOnOutlined,
  PhoneAndroidOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import { loadUsersMap, getUserDisplayName, resolveUnknownUsers } from "../SababishaAdmin/userResolver";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { walletGraphqlUrl } from "../../config";
import { tokens } from "../../theme";
import {
  walletQuery,
  WALLET_LEDGER_QUERY,
  ACTIVE_WITHDRAWAL_FEE_SCHEDULE_QUERY,
  WITHDRAWAL_FEE_TIERS_QUERY,
} from "../../data/WalletService/walletGraphQL";
import { formatKES } from "./WalletBalance";

// Mapping transaction types to styles, labels, and icons
const TRANSACTION_CONFIG = {
  TOPUP_MPESA: {
    label: "M-Pesa Top Up",
    color: "success",
    isCredit: true,
    bg: "rgba(34, 191, 114, 0.12)",
    textColor: "#1b995b",
    icon: <CreditCardOutlined fontSize="small" />,
  },
  TOPUP_MANUAL: {
    label: "Manual Deposit",
    color: "success",
    isCredit: true,
    bg: "rgba(34, 191, 114, 0.12)",
    textColor: "#1b995b",
    icon: <CreditCardOutlined fontSize="small" />,
  },
  DISBURSEMENT_ESCROW: {
    label: "Disbursement Escrow",
    color: "warning",
    isCredit: false,
    bg: "rgba(237, 108, 2, 0.12)",
    textColor: "#ed6c02",
    icon: <HourglassEmptyOutlined fontSize="small" />,
  },
  DISBURSEMENT_SENT: {
    label: "Disbursement Sent",
    color: "primary",
    isCredit: false,
    bg: "rgba(23, 102, 162, 0.12)",
    textColor: "#1766a2",
    icon: <CheckCircleOutlined fontSize="small" />,
  },
  DISBURSEMENT_REVERSAL: {
    label: "Disbursement Reversal",
    color: "secondary",
    isCredit: true,
    bg: "rgba(156, 39, 176, 0.12)",
    textColor: "#9c27b0",
    icon: <UndoOutlined fontSize="small" />,
  },
  FEE_CHARGE: {
    label: "Sababisha Fee (SSL)",
    color: "error",
    isCredit: false,
    bg: "rgba(211, 47, 47, 0.12)",
    textColor: "#d32f2f",
    icon: <ReceiptLongOutlined fontSize="small" />,
  },
  MPESA_CHARGE: {
    label: "Safaricom Fee (M)",
    color: "warning",
    isCredit: false,
    bg: "rgba(237, 108, 2, 0.12)",
    textColor: "#ed6c02",
    icon: <ReceiptLongOutlined fontSize="small" />,
  },
  OVERDRAFT_DRAWDOWN: {
    label: "Overdraft Drawdown",
    color: "info",
    isCredit: true,
    bg: "rgba(23, 102, 162, 0.12)",
    textColor: "#1766a2",
    icon: <CreditCardOutlined fontSize="small" />,
  },
  OVERDRAFT_REPAYMENT_INTEREST: {
    label: "OD Interest Repaid",
    color: "error",
    isCredit: false,
    bg: "rgba(211, 47, 47, 0.12)",
    textColor: "#d32f2f",
    icon: <ReceiptLongOutlined fontSize="small" />,
  },
  OVERDRAFT_REPAYMENT_PRINCIPAL: {
    label: "OD Principal Repaid",
    color: "warning",
    isCredit: false,
    bg: "rgba(237, 108, 2, 0.12)",
    textColor: "#ed6c02",
    icon: <CreditCardOutlined fontSize="small" />,
  },
  WITHDRAWAL_INITIATED: {
    label: "Withdrawal Debited",
    color: "warning",
    isCredit: false,
    bg: "rgba(237, 108, 2, 0.12)",
    textColor: "#ed6c02",
    icon: <MonetizationOnOutlined fontSize="small" />,
  },
  ROYALTY_CHARGE: {
    label: "Withdrawal Royalty",
    color: "error",
    isCredit: false,
    bg: "rgba(211, 47, 47, 0.12)",
    textColor: "#d32f2f",
    icon: <ReceiptLongOutlined fontSize="small" />,
  },
  ACTIVATION_FEE: {
    label: "Activation Fee",
    color: "error",
    isCredit: false,
    bg: "rgba(211, 47, 47, 0.12)",
    textColor: "#d32f2f",
    icon: <ReceiptLongOutlined fontSize="small" />,
  },
  EARLY_EXIT_FEE: {
    label: "Early Exit Fee",
    color: "error",
    isCredit: false,
    bg: "rgba(211, 47, 47, 0.12)",
    textColor: "#d32f2f",
    icon: <ReceiptLongOutlined fontSize="small" />,
  },
  WITHDRAWAL: {
    label: "Withdrawal",
    color: "warning",
    isCredit: false,
    bg: "rgba(237, 108, 2, 0.12)",
    textColor: "#ed6c02",
    icon: <MonetizationOnOutlined fontSize="small" />,
  },
  WITHDRAWAL_FEE: {
    label: "Withdrawal Fee",
    color: "error",
    isCredit: false,
    bg: "rgba(211, 47, 47, 0.12)",
    textColor: "#d32f2f",
    icon: <ReceiptLongOutlined fontSize="small" />,
  },
  C2B_TOPUP: {
    label: "Customer Top Up",
    color: "success",
    isCredit: true,
    bg: "rgba(34, 191, 114, 0.12)",
    textColor: "#1b995b",
    icon: <CreditCardOutlined fontSize="small" />,
  },
  TOP_UP: {
    label: "Top Up",
    color: "success",
    isCredit: true,
    bg: "rgba(34, 191, 114, 0.12)",
    textColor: "#1b995b",
    icon: <CreditCardOutlined fontSize="small" />,
  },
  DISBURSEMENT: {
    label: "Disbursement",
    color: "primary",
    isCredit: false,
    bg: "rgba(23, 102, 162, 0.12)",
    textColor: "#1766a2",
    icon: <CheckCircleOutlined fontSize="small" />,
  },
};

const calculateChargesFromTiers = (amount, tiers) => {
  if (amount == null || !Array.isArray(tiers) || tiers.length === 0) return null;
  const numAmount = Number(amount);
  if (isNaN(numAmount) || numAmount < 0) return null;

  const sorted = [...tiers].sort((a, b) => Number(b.amountFrom || 0) - Number(a.amountFrom || 0));
  const matched = sorted.find((t) => numAmount >= Number(t.amountFrom || 0));
  if (!matched) return null;

  const isPct = String(matched.chargeType || "").toUpperCase() === "PERCENTAGE";
  const sab = isPct
    ? Math.round(((numAmount * Number(matched.sababishaCharge || 0)) / 100) * 100) / 100
    : Number(matched.sababishaCharge || 0);
  const mpesa = isPct
    ? Math.round(((numAmount * Number(matched.mpesaCharge || 0)) / 100) * 100) / 100
    : Number(matched.mpesaCharge || 0);
  const total = sab + mpesa;

  return {
    sababishaCharge: sab,
    mpesaCharge: mpesa,
    totalCharge: total,
  };
};

const WalletLedger = ({ companyId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [transactions, setTransactions] = useState([]);
  const [userMap, setUserMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    loadUsersMap()
      .then((map) => {
        if (map instanceof Map) setUserMap(map);
      })
      .catch((err) => console.warn("[WalletLedger] loadUsersMap error:", err));
  }, []);

  const fetchLedger = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [gqlData, wRes, feeTiers] = await Promise.all([
        walletQuery(WALLET_LEDGER_QUERY, {
          companyId: parseInt(companyId, 10),
          page: 1,
          pageSize: 500,
        }),
        fetchJsonDynamic("walletWithdrawal", "wallet", { iscancelled: 0 }, walletGraphqlUrl.uri).catch((err) => {
          console.warn("[WalletLedger] Error fetching walletWithdrawal:", err);
          return null;
        }),
        walletQuery(ACTIVE_WITHDRAWAL_FEE_SCHEDULE_QUERY)
          .then(async (schedRes) => {
            const activeSched = schedRes?.activeWithdrawalFeeSchedule;
            if (activeSched?.id) {
              const tiersRes = await walletQuery(WITHDRAWAL_FEE_TIERS_QUERY, { scheduleId: activeSched.id });
              return tiersRes?.withdrawalFeeTiers || [];
            }
            return [];
          })
          .catch((err) => {
            console.warn("[WalletLedger] Error fetching active withdrawal fee tiers:", err);
            return [];
          }),
      ]);

      const withdrawalMap = new Map();
      const unknownUserIds = new Set();

      if (wRes?.results) {
        try {
          const wList = Array.isArray(wRes.results)
            ? wRes.results
            : typeof wRes.results === "string"
            ? JSON.parse(wRes.results)
            : [];
          if (Array.isArray(wList)) {
            wList.forEach((w) => {
              const wid = Number(w.Id ?? w.id);
              if (wid) withdrawalMap.set(wid, w);
              if (w.approved_by || w.approvedby) unknownUserIds.add(Number(w.approved_by || w.approvedby));
              if (w.createdby) unknownUserIds.add(Number(w.createdby));
            });
          }
        } catch (e) {
          console.warn("[WalletLedger] Error parsing withdrawals:", e);
        }
      }

      const entries = gqlData?.walletLedger;
      if (Array.isArray(entries)) {
        const rows = entries.map((e) => {
          const rawAmount = Number(e.amount || 0);
          const after = Number(e.balanceAfter || 0);
          const cfg = TRANSACTION_CONFIG[e.txType] || {};
          const isCredit = cfg.isCredit !== undefined ? cfg.isCredit : rawAmount > 0;
          const absAmount = Math.abs(rawAmount);

          // Find matching withdrawal record
          const refIdNum = Number(e.refId);
          const matchedWithdrawal =
            (refIdNum && withdrawalMap.get(refIdNum)) ||
            (e.txType?.includes("WITHDRAWAL") && withdrawalMap.get(Number(e.id))) ||
            null;

          // Resolve recipient
          let recipientVal = e.recipient || "";
          if (!recipientVal && matchedWithdrawal) {
            recipientVal =
              matchedWithdrawal.phone_number ||
              matchedWithdrawal.PhoneNumber ||
              matchedWithdrawal.destination_phone ||
              matchedWithdrawal.bank_account_number ||
              "";
          }
          if (!recipientVal && e.description) {
            const match = e.description.match(/(?:to|from|for|phone:?)\s*([0-9]{9,12})/i);
            if (match) recipientVal = match[1];
          }
          if (!recipientVal && (e.txType?.includes("TOPUP") || e.txType?.includes("TOP_UP"))) {
            recipientVal = "Customer M-Pesa";
          }
          if (!recipientVal) recipientVal = "—";

          // Resolve approvedBy (prefer numeric ID, never display raw ciphertext)
          let approverVal = e.approvedById ? String(e.approvedById) : "";
          if (!approverVal && e.approvedBy && !isNaN(Number(e.approvedBy))) {
            approverVal = String(e.approvedBy);
          }
          if (!approverVal && matchedWithdrawal) {
            const mApproved = matchedWithdrawal.approved_by || matchedWithdrawal.approvedby;
            if (mApproved && !isNaN(Number(mApproved))) {
              approverVal = String(mApproved);
            } else if (matchedWithdrawal.createdby && !isNaN(Number(matchedWithdrawal.createdby))) {
              approverVal = String(matchedWithdrawal.createdby);
            }
          }
          if (!approverVal && e.createdBy && (e.txType?.includes("TOPUP") || e.txType?.includes("WITHDRAWAL"))) {
            approverVal = String(e.createdBy);
          }
          if (approverVal && !isNaN(Number(approverVal))) {
            unknownUserIds.add(Number(approverVal));
          }
          if (!approverVal) approverVal = "—";

          // Resolve Sababisha & M-Pesa fees based on active tariffs or backend data
          let sababishaFee = (e.sababishaCharge != null && e.sababishaCharge !== "") ? Number(e.sababishaCharge) : null;
          let mpesaFee = (e.mpesaCharge != null && e.mpesaCharge !== "") ? Number(e.mpesaCharge) : null;

          const isWithdrawalRelated = e.txType?.includes("WITHDRAWAL") || e.txType === "FEE_CHARGE";
          if ((sababishaFee == null || mpesaFee == null) && isWithdrawalRelated) {
            const withdrawalAmount = matchedWithdrawal?.amount
              ? Number(matchedWithdrawal.amount)
              : absAmount;

            const calc = calculateChargesFromTiers(withdrawalAmount, feeTiers);
            if (calc) {
              if (sababishaFee == null) sababishaFee = calc.sababishaCharge;
              if (mpesaFee == null) mpesaFee = calc.mpesaCharge;
            }
          }

          if (sababishaFee == null && matchedWithdrawal) {
            const rawSab = matchedWithdrawal.withdrawal_fee ?? matchedWithdrawal.WithdrawalFee ?? matchedWithdrawal.sababisha_charge;
            if (rawSab != null) sababishaFee = Number(rawSab);
          }
          if (mpesaFee == null && matchedWithdrawal) {
            const rawMpesa = matchedWithdrawal.mpesa_fee ?? matchedWithdrawal.MpesaFee ?? matchedWithdrawal.mpesa_charge;
            if (rawMpesa != null) mpesaFee = Number(rawMpesa);
          }

          if (e.txType === "FEE_CHARGE" && sababishaFee == null) {
            sababishaFee = absAmount;
          }
          if (e.txType === "MPESA_CHARGE" && mpesaFee == null) {
            mpesaFee = absAmount;
          }

          const totalFee = (sababishaFee != null || mpesaFee != null)
            ? (Number(sababishaFee || 0) + Number(mpesaFee || 0))
            : null;

          return {
            id: e.id,
            transactionType: e.txType,
            recipient: recipientVal,
            approvedBy: approverVal,
            approvedById: e.approvedById,
            amount: absAmount,
            rawAmount,
            isCredit,
            balanceAfter: after,
            balanceBefore: isCredit ? after - absAmount : after + absAmount,
            referenceType: e.refType,
            referenceId: e.refId,
            description: e.description,
            glJournalId: e.glJournalId,
            createdAt: e.createdDate,
            sababishaCharge: sababishaFee,
            mpesaCharge: mpesaFee,
            totalCharge: totalFee,
          };
        });

        // Resolve any unknown user IDs asynchronously to keep userMap fresh
        if (unknownUserIds.size > 0) {
          resolveUnknownUsers(Array.from(unknownUserIds))
            .then((updatedMap) => {
              if (updatedMap instanceof Map) setUserMap(new Map(updatedMap));
            })
            .catch(() => {});
        }

        setTransactions(rows);
        return;
      }
      setTransactions([]);
    } catch (gqlErr) {
      console.warn("walletLedger GQL failed:", gqlErr);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
  }, [companyId]);

  // Client-side filtering fallback for search/type filter
  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) => {
      const matchesSearch =
        !searchQuery ||
        (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.referenceId && String(t.referenceId).includes(searchQuery)) ||
        (t.recipient && t.recipient.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.approvedBy && t.approvedBy.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.mpesaTransactionId &&
          t.mpesaTransactionId.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesType =
        typeFilter === "ALL" ||
        (typeFilter === "CREDIT" && (t.transactionType.includes("TOPUP") || t.transactionType.includes("REVERSAL"))) ||
        (typeFilter === "DEBIT" && (t.transactionType.includes("DISBURSEMENT") || t.transactionType.includes("WITHDRAWAL"))) ||
        (typeFilter === "FEES" && (t.transactionType.includes("FEE") || t.transactionType.includes("ROYALTY"))) ||
        t.transactionType === typeFilter;

      return matchesSearch && matchesType;
    });
  }, [transactions, searchQuery, typeFilter]);

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ["ID,Date,Type,Recipient,Approver,Amount (KES),Sababisha Fee (KES),M-Pesa Fee (KES),Total Fee (KES),Balance Before,Balance After,Reference Type,Reference ID,M-Pesa Receipt,Description"];
    const rows = filteredTransactions.map((t) => {
      const isCipher = typeof t.approvedBy === "string" && t.approvedBy.length > 15 && !t.approvedBy.includes(" ") && /[+=/]/.test(t.approvedBy);
      const approverText = (!t.approvedBy || t.approvedBy === "—" || isCipher)
        ? "—"
        : (isNaN(Number(t.approvedBy)) ? t.approvedBy : getUserDisplayName(t.approvedBy, userMap));
      return [
        t.id,
        `"${new Date(t.createdAt).toLocaleString()}"`,
        t.transactionType,
        `"${(t.recipient || "—").replace(/"/g, '""')}"`,
        `"${(approverText || "—").replace(/"/g, '""')}"`,
        t.amount,
        t.sababishaCharge != null ? t.sababishaCharge : "",
        t.mpesaCharge != null ? t.mpesaCharge : "",
        t.totalCharge != null ? t.totalCharge : "",
        t.balanceBefore,
        t.balanceAfter,
        t.referenceType || "",
        t.referenceId || "",
        t.mpesaTransactionId || "",
        `"${(t.description || "").replace(/"/g, '""')}"`,
      ].join(",");
    });

    const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `wallet_ledger_co_${companyId}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const ledgerColumns = useMemo(
    () => [
      {
        field: "createdAt",
        headerName: "Date & Time",
        width: 170,
        renderCell: (params) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {new Date(params.value).toLocaleDateString()}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              {new Date(params.value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </Typography>
          </Box>
        ),
      },
      {
        field: "transactionType",
        headerName: "Transaction Type",
        width: 190,
        renderCell: (params) => {
          const cfg = TRANSACTION_CONFIG[params.value] || {
            label: params.value,
            color: "default",
            isCredit: false,
            bg: "rgba(255,255,255,0.08)",
            textColor: colors.grey[200],
            icon: <ReceiptLongOutlined fontSize="small" />,
          };
          return (
            <Chip
              icon={cfg.icon}
              label={cfg.label}
              size="small"
              sx={{
                backgroundColor: cfg.bg,
                color: cfg.textColor,
                fontWeight: 700,
                fontSize: "0.72rem",
                borderRadius: 1.5,
                "& .MuiChip-icon": { color: cfg.textColor },
              }}
            />
          );
        },
      },
      {
        field: "recipient",
        headerName: "Recipient",
        width: 165,
        renderCell: (params) => {
          const val = params.value || params.row.recipient;
          if (!val || val === "—") {
            return (
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                —
              </Typography>
            );
          }
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "0.86rem",
                color: isDark ? "#ffffff" : "#0f172a",
                letterSpacing: "0.01em",
              }}
            >
              {val}
            </Typography>
          );
        },
      },
      {
        field: "approvedBy",
        headerName: "Approver",
        width: 165,
        renderCell: (params) => {
          const raw = params.value || params.row.approvedBy;
          const isCiphertext = typeof raw === "string" && raw.length > 15 && !raw.includes(" ") && /[+=/]/.test(raw);
          const display = (!raw || raw === "—" || isCiphertext)
            ? "—"
            : (isNaN(Number(raw)) ? raw : getUserDisplayName(raw, userMap));
          if (display === "—") {
            return (
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                —
              </Typography>
            );
          }
          return (
            <Chip
              icon={<VerifiedUserOutlined fontSize="small" />}
              label={display}
              size="small"
              sx={{
                backgroundColor: isDark ? "rgba(34, 191, 114, 0.15)" : "#e8f8f0",
                color: isDark ? "#4ade80" : "#1b995b",
                fontWeight: 600,
                fontSize: "0.75rem",
                borderRadius: 1.5,
                "& .MuiChip-icon": { color: isDark ? "#4ade80" : "#1b995b" },
              }}
            />
          );
        },
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 140,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          const isCredit = params.row.isCredit;
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: isCredit
                  ? "#22bf72"
                  : params.row.transactionType === "DISBURSEMENT_ESCROW"
                  ? "#ed6c02"
                  : colors.grey[100],
              }}
            >
              {isCredit ? "+" : "-"}
              {formatKES(params.value)}
            </Typography>
          );
        },
      },
      {
        field: "sababishaCharge",
        headerName: "Sababisha (S)",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          const val = params.value != null ? params.value : params.row.sababisha_charge;
          return (
            <Typography variant="body2" sx={{ fontSize: "0.8rem", color: val != null ? "#1766a2" : colors.grey[400] }}>
              {val != null ? formatKES(val) : "—"}
            </Typography>
          );
        },
      },
      {
        field: "mpesaCharge",
        headerName: "M-Pesa (M)",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          const val = params.value != null ? params.value : params.row.mpesa_charge;
          return (
            <Typography variant="body2" sx={{ fontSize: "0.8rem", color: val != null ? "#e65100" : colors.grey[400] }}>
              {val != null ? formatKES(val) : "—"}
            </Typography>
          );
        },
      },
      {
        field: "totalCharge",
        headerName: "Total Fee",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          const val = params.value != null ? params.value : params.row.total_charge;
          return (
            <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 700, color: val != null ? "#d32f2f" : colors.grey[400] }}>
              {val != null ? formatKES(val) : "—"}
            </Typography>
          );
        },
      },
      {
        field: "balanceProgression",
        headerName: "Balance Progression",
        width: 200,
        valueGetter: (params) => `${params.row.balanceBefore} -> ${params.row.balanceAfter}`,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              {formatKES(params.row.balanceBefore)}
            </Typography>
            <ArrowForwardOutlined sx={{ fontSize: 13, color: colors.grey[400] }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[200] }}>
              {formatKES(params.row.balanceAfter)}
            </Typography>
          </Box>
        ),
      },
      {
        field: "reference",
        headerName: "Reference",
        width: 160,
        renderCell: (params) => {
          const { referenceType, referenceId } = params.row;
          return referenceType && referenceId ? (
            <Chip
              label={`${referenceType} #${referenceId}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 22 }}
            />
          ) : (
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              —
            </Typography>
          );
        },
      },
      {
        field: "mpesaTransactionId",
        headerName: "M-Pesa Receipt",
        width: 160,
        renderCell: (params) => {
          return params.value ? (
            <Typography
              component="span"
              sx={{
                fontFamily: "monospace",
                fontWeight: 700,
                fontSize: "0.8rem",
                color: "#1f76ad",
                backgroundColor: isDark ? "rgba(31, 118, 173, 0.15)" : "#e8f4fc",
                px: 0.8,
                py: 0.3,
                borderRadius: 1,
              }}
            >
              {params.value}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              —
            </Typography>
          );
        },
      },
      {
        field: "description",
        headerName: "Description",
        width: 280,
        renderCell: (params) => (
          <Tooltip title={params.value || ""}>
            <Typography variant="body2" noWrap sx={{ color: colors.grey[300], fontSize: "0.82rem" }}>
              {params.value || "—"}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [colors, isDark, userMap]
  );

  return (
    <Card
      sx={{
        borderRadius: 2.5,
        backgroundColor: isDark ? colors.primary[400] : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        boxShadow: "none",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
        {/* Ledger Header & Search Controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
              Wallet Transaction Ledger
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              Immutable append-only record of all balance movements, escrows, and fee charges.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title="Export ledger to CSV">
              <Button
                variant="outlined"
                size="small"
                onClick={handleExportCSV}
                startIcon={<DownloadOutlined />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: isDark ? "rgba(255,255,255,0.2)" : "#cbd5e1",
                  color: colors.grey[200],
                }}
              >
                Export CSV
              </Button>
            </Tooltip>
            <Tooltip title="Refresh ledger">
              <IconButton
                onClick={fetchLedger}
                size="small"
                sx={{
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1"}`,
                  borderRadius: 1.5,
                }}
              >
                {loading ? <CircularProgress size={18} /> : <RefreshOutlined fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Filter Toolbar */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mb: 2.5,
            alignItems: "center",
          }}
        >
          {/* Search Input */}
          <TextField
            size="small"
            placeholder="Search reference, receipt, description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: colors.grey[400] }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Type Filter Dropdown */}
          <TextField
            select
            size="small"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListOutlined sx={{ color: colors.grey[400], fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="ALL">All Transactions</MenuItem>
            <MenuItem value="CREDIT">Top-Ups & Credits Only</MenuItem>
            <MenuItem value="DEBIT">Disbursements & Debits</MenuItem>
            <MenuItem value="FEES">Fees & Royalties Only</MenuItem>
            <MenuItem value="TOPUP_MPESA">M-Pesa Top Up</MenuItem>
            <MenuItem value="TOPUP_MANUAL">Manual Deposit</MenuItem>
            <MenuItem value="DISBURSEMENT_SENT">Disbursement Sent</MenuItem>
            <MenuItem value="DISBURSEMENT_ESCROW">Disbursement Escrow</MenuItem>
            <MenuItem value="DISBURSEMENT_REVERSAL">Disbursement Reversal</MenuItem>
            <MenuItem value="FEE_CHARGE">Platform Fee</MenuItem>
            <MenuItem value="WITHDRAWAL_INITIATED">Withdrawal Request</MenuItem>
          </TextField>
        </Box>

        {/* Transactions DataGrid */}
        <Box
          sx={{
            width: "100%",
            "& .MuiDataGrid-root": {
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
              borderRadius: 2,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDark ? colors.primary[500] : "#f1f5f9",
              fontWeight: 700,
              borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#cbd5e1"}`,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9"}`,
            },
            "& .MuiDataGrid-virtualScroller": {
              "&::-webkit-scrollbar": {
                height: "12px",
                width: "12px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0",
                borderRadius: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: isDark ? "rgba(255,255,255,0.25)" : "#94a3b8",
                borderRadius: "6px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: isDark ? "rgba(255,255,255,0.35)" : "#64748b",
              },
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            },
          }}
        >
          <DataGrid
            autoHeight
            rows={filteredTransactions}
            columns={ledgerColumns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50]}
            loading={loading}
            disableSelectionOnClick
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default WalletLedger;
